import { forwardRef, useState, useRef } from 'react'

import _ from 'lodash'
import { styled } from 'styled-components'

import { withLabel, withFormValue } from 'peach/decorators'

import KeyboardNavArea from '../$internal/KeyboardNavArea'

import filterOptions from './filterOptions'
import formatOptions from './formatOptions'
import SelectBottomControls from './SelectBottomControls'
import SelectFrame from './SelectFrame'
import SelectItem from './SelectItem'
import SelectTopSearch from './SelectTopSearch'

const Content = styled.div`
  padding: 4px;
  overflow-y: auto;
`

const Check = styled.span`
  display: inline-block;
  transition: opacity 0.2s;
  opacity: ${(p) => (p.isChecked ? '1' : '0')};
  line-height: 1;
  color: ${(p) => p.theme.highlight};
  font-size: 16px;
`

const SingleSelect = forwardRef((props, ref) => {
  const {
    options,
    value: selectedValue,
    onChange,
    contentRef,
    notes,
    nullable,
    searchable,
    loading,
    disabled,
    label,
    prompt,
    ...rest
  } = props

  const inputRef = useRef()

  const formattedOptions = formatOptions(options, { notes })

  const [query, setQuery] = useState('')

  const filteredOptions = filterOptions(formattedOptions, query)

  const renderContents = ({ onClose }) => {
    const contents = _.map(filteredOptions, (option) => {
      const { label, value, note, disabled } = option
      const isChecked = selectedValue === value

      const handleClick = () => {
        onChange(value)
        if (onClose) onClose()
      }

      return (
        <SelectItem
          disabled={disabled}
          isSelected={isChecked}
          key={value}
          label={label}
          left={<Check isChecked={isChecked}>{'›' || '✓' || '✔︎'}</Check>}
          note={note}
          onClick={handleClick}
        />
      )
    })

    return <Content ref={contentRef}>{contents}</Content>
  }

  const selectedOption = _.find(filteredOptions, { value: selectedValue })

  const triggerLabel = loading
    ? 'Loading…'
    : selectedOption?.label || prompt || `Select${label ? ` ${label}` : ''}`

  const searchControls = searchable && (
    <SelectTopSearch onChange={setQuery} ref={inputRef} value={query} />
  )

  const renderControls = ({ onClose }) => {
    const handleClear = () => {
      onChange(null)
      onClose()
    }

    return (
      <SelectBottomControls canClear={!!selectedValue} onClear={handleClear} />
    )
  }

  const handleTypeCharacter = () => {
    if (searchable && inputRef.current) {
      inputRef.current.focus()
    }
  }

  return (
    <SelectFrame
      {...rest}
      controls={nullable && (({ onClose }) => renderControls({ onClose }))}
      disabled={loading || disabled}
      label={label}
      render={({ onClose }) => (
        <KeyboardNavArea onTypeCharacter={handleTypeCharacter}>
          {renderContents({ onClose })}
        </KeyboardNavArea>
      )}
      searchControls={searchControls}
      triggerHasValue={!!selectedOption}
      triggerLabel={triggerLabel}
      triggerRef={ref}
    />
  )
})

SingleSelect.displayName = 'SingleSelect'

export { SingleSelect as RawSingleSelect }

export default withFormValue(withLabel(SingleSelect))
